import { ReduxState } from 'react-admin';
import { useSelector } from 'react-redux';

/**
 * Which point of view the app is being read from.
 *
 * Consumer and Service Owner are the two sides of the same platform. Admin is
 * not a third side: it is the Service Owner view widened from one team to every
 * team, which is why it is a point of view rather than a page of its own.
 */
export type Pov = 'CONSUMER' | 'SERVICE_OWNER' | 'ADMIN';

export const ADMIN_POV = 'ADMIN';

/**
 * Whether this user may read the platform rather than their own team.
 *
 * `is_staff` and not `is_superuser`: the API gates every `all_teams=true`
 * endpoint on `is_staff` alone, and the two flags genuinely differ — a
 * superuser without `is_staff` is refused. Offering the switch on the other
 * flag would hand it to people the API will 403 and withhold it from staff who
 * would be served.
 */
export const isPlatformAdmin = (): boolean => {
    try {
        return JSON.parse(localStorage.getItem('user') as string)?.is_staff === true;
    } catch {
        // no user stored yet, or something that is not JSON
        return false;
    }
};

/**
 * The point of view in force.
 *
 * localStorage first and the store second, which is how every reader of this
 * value has always resolved it: `pov_preference` is written on login and on
 * every switch, so in practice it is the answer and the store is the fallback
 * for the render before it exists. The store is still read — and still
 * subscribed to — because dispatching is what re-renders everyone after a
 * switch.
 */
export const usePov = (): Pov => {
    const stored = localStorage.getItem('pov_preference');
    const fromStore = useSelector((state: ReduxState) => state.ui.viewMode);
    const pov = (stored || fromStore) as Pov;

    /* A preference outlives the user who set it: logging out clears the token
       and the user but not this, so the next person at the same browser would
       inherit Admin. What the switch offers is checked here rather than trusted
       from storage. */
    if (pov === ADMIN_POV && !isPlatformAdmin()) return 'SERVICE_OWNER';
    return pov;
};

/**
 * Which point of view's resources the navigation draws.
 *
 * Nothing is tagged `viewMode: 'ADMIN'`, and nothing should be: Admin widens
 * what the Service Owner view shows rather than adding pages to it, so the rail
 * keeps the shape it has. Only the dashboard reads the platform today, so the
 * pages reached from here stay scoped to the user's own team.
 */
export const navPov = (pov: Pov): 'CONSUMER' | 'SERVICE_OWNER' =>
    (pov === ADMIN_POV ? 'SERVICE_OWNER' : pov);
